import { createPortal } from 'react-dom';
import useResponsive from '@/hooks/useResponsive';
import styles from './filter-modal.module.scss';
import MultiOptionInput from '../../MultiOptionInput';
import Modal from '../../Modal/modal';
import Button from '../../Button';
import { Dispatch, FC, SetStateAction } from 'react';
import { getTodayStr } from '@/utils/functions';
import { FilterModalOptions } from '../utils';

interface FilterModalProps {
  countries: string[];
  sources: string[];
  showOptions: string | null;
  setShowOptions: Dispatch<SetStateAction<FilterModalOptions>>;
  selectedCountries: string[];
  selectedSources: string[];
  earliestDate: string;
  latestDate: string;
  isSingleDate: boolean;
  setIsSingleDate: Dispatch<SetStateAction<boolean>>;
  setSelectedCountries: Dispatch<SetStateAction<string[]>>;
  setSelectedSources: Dispatch<SetStateAction<string[]>>;
  setEarliestDate: Dispatch<SetStateAction<string>>;
  setLatestDate: Dispatch<SetStateAction<string>>;
}

const TITLES: Record<string, string> = {
  countries: 'Countries',
  sources: 'Sources',
  date: 'Publish dates',
};

/**
 * Filters apply as soon as they change: they only scope the next prompt, so
 * there is nothing to confirm. "Done", Escape and the backdrop all just close.
 * On desktop every filter shows at once; on mobile only the one that was tapped.
 */
const FilterModal: FC<FilterModalProps> = ({
  countries,
  sources,
  showOptions,
  setShowOptions,
  selectedCountries,
  selectedSources,
  setSelectedCountries,
  setSelectedSources,
  isSingleDate,
  setIsSingleDate,
  earliestDate,
  latestDate,
  setEarliestDate,
  setLatestDate,
}) => {
  const closePopover = () => {
    setShowOptions(null);
  };
  const { isMobile } = useResponsive();

  if (showOptions === null) return null;

  const today = getTodayStr();
  const shouldShowOnDesktop = !isMobile && showOptions !== null;
  const onCheckboxChange = (name: string, value: string, selected: boolean) => {
    let selectedItems: string[] = [];
    let updateFn;

    if (name === 'countries') {
      selectedItems = [...selectedCountries];
      updateFn = setSelectedCountries;
    } else if (name === 'sources') {
      selectedItems = [...selectedSources];
      updateFn = setSelectedSources;
    } else {
      return;
    }

    if (selected) {
      selectedItems.push(value);
    } else {
      selectedItems = selectedItems.filter((item) => item !== value);
    }

    updateFn(selectedItems);
  };

  const showsSection = (section: string) =>
    shouldShowOnDesktop || showOptions === section;

  // Resets only the sections on screen, so on mobile it never touches
  // filters the person can't see
  const clearVisible = () => {
    if (showsSection('countries')) setSelectedCountries([]);
    if (showsSection('sources')) setSelectedSources([]);
    if (showsSection('date')) {
      setIsSingleDate(true);
      setEarliestDate('');
      setLatestDate(today);
    }
  };

  return createPortal(
    <Modal
      size="large"
      isOpen={true}
      title={shouldShowOnDesktop ? 'Filters' : TITLES[showOptions]}
      onClose={closePopover}
    >
      <div className={styles['filter-modal']}>
        <div className={styles['filter-sections']}>
          {(showOptions === 'countries' || shouldShowOnDesktop) && (
            <MultiOptionInput
              options={countries}
              selectedOptions={selectedCountries}
              name="countries"
              handleChange={onCheckboxChange}
            />
          )}
          {(showOptions === 'sources' || shouldShowOnDesktop) && (
            <MultiOptionInput
              options={sources}
              selectedOptions={selectedSources}
              name="sources"
              handleChange={onCheckboxChange}
            />
          )}
          {(showOptions === 'date' || shouldShowOnDesktop) && (
            <div className={styles['date-wrapper']}>
              <strong>Publish dates</strong>
              <div className={styles['date-type']}>
                <label>
                  <input
                    type="radio"
                    name="dateSelection"
                    value="singleDate"
                    checked={isSingleDate}
                    onChange={(event) => setIsSingleDate(event.target.checked)}
                  />
                  Single date
                </label>
                <label>
                  <input
                    type="radio"
                    name="dateSelection"
                    value="dateInterval"
                    checked={!isSingleDate}
                    onChange={(event) => setIsSingleDate(!event.target.checked)}
                  />
                  Date range
                </label>
              </div>

              <div className={styles['date-picker']}>
                {!isSingleDate && (
                  <input
                    type="date"
                    aria-label="From"
                    value={earliestDate}
                    max={latestDate}
                    onChange={(event) => {
                      setEarliestDate(event.target.value);
                    }}
                  />
                )}

                {!isSingleDate && <span>to</span>}
                <input
                  type="date"
                  aria-label={isSingleDate ? 'Date' : 'To'}
                  value={latestDate}
                  min={isSingleDate ? '' : earliestDate}
                  max={today}
                  onChange={(event) => {
                    setLatestDate(event.target.value || today);
                  }}
                />
              </div>
            </div>
          )}
        </div>

        <div className={styles['filter-footer']}>
          <button
            type="button"
            className={styles['clear-btn']}
            onClick={clearVisible}
          >
            {shouldShowOnDesktop ? 'Clear all' : 'Clear'}
          </button>
          <span className={styles['hint']}>Changes apply as you select</span>
          <Button className={styles['done-btn']} onClick={closePopover}>
            Done
          </Button>
        </div>
      </div>
    </Modal>,
    document.body
  );
};

export default FilterModal;
